import React from "react";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Chip,
  Divider,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ScaleIcon from "@mui/icons-material/Scale";
import StraightenIcon from "@mui/icons-material/Straighten";
import ReadOnlyFieldGroup from "./readOnlyFields";
import WarehouseInventory from "./warehouseInventory";
import { formatDate } from "../../utils/formatDate";

const SubSku = ({ sub, isMaster, currency, costCurrency }) => (
  <Grid item xs={12}>
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        border: "1px solid var(--border-color)",
        borderRadius: 1,
        background: "var(--bg-paper)",
        borderLeft: "3px solid #1976d2",
      }}
    >
      <Typography
        fontFamily="Poppins"
        fontSize={13}
        fontWeight={600}
        color="#1976d2"
        sx={{ mb: 1 }}
      >
        {sub.title}
      </Typography>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>
          <ReadOnlyFieldGroup label="Title" value={sub.title} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ReadOnlyFieldGroup label="SKU" value={sub.sku} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ReadOnlyFieldGroup
            label="Price"
            value={sub.price}
            currency={currency}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ReadOnlyFieldGroup
            label="Cost"
            value={sub.cost}
            currency={costCurrency}
          />
        </Grid>

        {!isMaster && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="caption"
                fontFamily="Poppins"
                fontWeight={500}
                color="var(--text-secondary)"
              >
                Additional Details
              </Typography>
            </Grid>
            {["hs_code", "hts_code", "country_of_origin"].map((field) => (
              <Grid item xs={12} sm={4} key={field}>
                <ReadOnlyFieldGroup
                  label={field.replace(/_/g, " ").toUpperCase()}
                  value={sub[field]}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={3}>
              <ReadOnlyFieldGroup label="Height" value={sub.height} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ReadOnlyFieldGroup label="Length" value={sub.length} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ReadOnlyFieldGroup label="Width" value={sub.width} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <ReadOnlyFieldGroup
                label="Dimension Unit"
                value={sub.height_unit || sub.length_unit || sub.width_unit}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyFieldGroup label="Weight" value={sub.weight} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <ReadOnlyFieldGroup label="Weight Unit" value={sub.weight_unit} />
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  </Grid>
);

const VariantCard = ({
  variant,
  allVariants,
  productId,
  currency,
  costCurrency,
}) => {
  const subSkus = allVariants.filter((s) => s.parent === variant.uid);

  return (
    <Card
      sx={{
        mt: 1,
        mb: 1,
        border: "1px solid var(--border-color)",
        background: "var(--bg-paper)",
        borderRadius: 2,
        boxShadow: 0,
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
            pb: 1,
            borderBottom: "1px solid var(--border-color)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              fontFamily="Poppins"
              fontSize={14}
              fontWeight={700}
              color="var(--text-primary)"
            >
              {variant.title}
            </Typography>
            <Typography
              variant="caption"
              color="var(--text-tertiary)"
              fontFamily="Poppins"
            >
              Variant
            </Typography>
          </Box>
          {(variant.isBundle || variant.isMaster) && subSkus.length > 0 && (
            <Chip
              icon={<InventoryIcon />}
              label={`${subSkus.length} Sub-SKU${subSkus.length > 1 ? "s" : ""}`}
              color="primary"
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600, "& .MuiChip-icon": { color: "inherit" } }}
            />
          )}
        </Box>

        {/* Main Fields */}
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12}>
            <Typography
              variant="caption"
              fontFamily="Poppins"
              fontWeight={600}
              color="var(--text-tertiary)"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 14, color: "#1976d2" }} />
              Basic Information
            </Typography>
          </Grid>

          {[
            "title",
            "sku",
            "hs_code",
            "hts_code",
            "country_of_origin",
            "price",
            // Unit cost, from Pipe17's per-SKU `cost`. Blank until Pipe17 is
            // actually populating it — the field is unset across this org's
            // whole catalog today, on both its integrations.
            "cost",
          ].map((field) => (
            <Grid item xs={6} sm={4} md={2} key={field}>
              <ReadOnlyFieldGroup
                label={field.replace(/_/g, " ").toUpperCase()}
                value={variant[field]}
                // Cost and price are priced in their own currencies — normally
                // the same, but they are separate fields on the core product
                // and must not be assumed identical. Non-money fields ignore it.
                currency={field === "cost" ? costCurrency : currency}
              />
            </Grid>
          ))}
        </Grid>

        {/* Dimensions Section */}
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          <Grid item xs={12}>
            <Typography
              variant="caption"
              fontFamily="Poppins"
              fontWeight={600}
              color="var(--text-tertiary)"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              <StraightenIcon sx={{ fontSize: 14, color: "#1976d2" }} />
              Dimensions & Weight
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <ReadOnlyFieldGroup label="Height" value={variant.height} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <ReadOnlyFieldGroup label="Length" value={variant.length} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <ReadOnlyFieldGroup label="Width" value={variant.width} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <ReadOnlyFieldGroup
              label="Dim Unit"
              value={
                variant.height_unit || variant.length_unit || variant.width_unit
              }
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <ReadOnlyFieldGroup label="Weight" value={variant.weight} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <ReadOnlyFieldGroup
              label="Weight Unit"
              value={variant.weight_unit}
            />
          </Grid>
        </Grid>

        {/* Extra Attributes Section */}
        {variant.option && variant.option.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              fontFamily="Poppins"
              fontWeight={600}
              color="var(--text-tertiary)"
              sx={{
                display: "block",
                mb: 0.75,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Extra Attributes
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {variant.option.map((k, ki) => {
                const label =
                  k.option_type && k.value
                    ? `${k.option_type}: ${k.value}`
                    : k.value || k.option_type || "—";
                return (
                  <Chip
                    key={ki}
                    label={label}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "11px",
                      height: 22,
                      borderColor: "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--bg-secondary)",
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        )}

        {/* Inventory Section */}
        {variant.full_inventory ? (
          <Box
            sx={{
              p: 1.5,
              mb: 1.5,
              background: "var(--bg-secondary)",
              borderRadius: 1,
              border: "1px solid var(--border-color)",
            }}
          >
            <Typography
              variant="caption"
              fontFamily="Poppins"
              fontWeight={600}
              color="var(--text-tertiary)"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              <InventoryIcon sx={{ fontSize: 13, color: "#7b1fa2" }} />
              Inventory Status
              {variant.full_inventory.warehouse_count > 1 && (
                <Typography
                  component="span"
                  variant="caption"
                  fontFamily="Poppins"
                  fontWeight={500}
                  color="var(--text-secondary)"
                  sx={{ textTransform: "none", letterSpacing: 0 }}
                >
                  (across {variant.full_inventory.warehouse_count} warehouses)
                </Typography>
              )}
            </Typography>
            <Grid container spacing={1.5}>
              {[
                {
                  label: "Total Qty",
                  value: variant.full_inventory.quantity || 0,
                  color: "#1976d2",
                },
                {
                  label: "In Hand",
                  value: variant.full_inventory.in_hand || 0,
                  color: "#388e3c",
                },
                {
                  label: "In Transit",
                  value: variant.full_inventory.in_transit || 0,
                  color: "#f57c00",
                },
                {
                  label: "Last PO Date",
                  value: variant.full_inventory.last_po_created_date
                    ? formatDate(variant.full_inventory.last_po_created_date)
                    : "N/A",
                  color: "#7b1fa2",
                },
              ].map(({ label, value, color }) => (
                <Grid item xs={6} sm={3} key={label}>
                  <Box
                    sx={{
                      p: 1,
                      backgroundColor: "var(--bg-paper)",
                      borderRadius: 1,
                      border: "1px solid #e1bee7",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="var(--text-secondary)"
                      fontFamily="Poppins"
                      fontWeight={500}
                      display="block"
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color={color}
                      fontFamily="Poppins"
                      fontWeight={700}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : null}

        {/* Per-warehouse breakdown behind the totals above. Lazy: it only
            queries /inventory when the operator expands it, so opening a
            product with many variants stays a single request. */}
        <WarehouseInventory productId={productId} sku={variant.sku} />

        {/* Bundle/Master Section */}
        {(variant.isBundle || variant.isMaster) && (
          <Box
            sx={{
              p: 1,
              mb: 1,
              backgroundColor: "var(--bg-secondary)",
              borderRadius: 1,
              border: "1px solid #ffcc02",
              display: "flex",
              gap: 2,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={variant.isBundle}
                  disabled
                  size="small"
                  sx={{
                    color: "#ff9800",
                    "&.Mui-checked": { color: "#ff9800" },
                  }}
                />
              }
              label={
                <Typography fontFamily="Poppins" fontSize={13} fontWeight={500}>
                  Bundle
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={variant.isMaster}
                  disabled
                  size="small"
                  sx={{
                    color: "#ff9800",
                    "&.Mui-checked": { color: "#ff9800" },
                  }}
                />
              }
              label={
                <Typography fontFamily="Poppins" fontSize={13} fontWeight={500}>
                  Master
                </Typography>
              }
            />
          </Box>
        )}

        {/* Sub-SKUs Section */}
        {(variant.isMaster || variant.isBundle) && subSkus.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              fontFamily="Poppins"
              fontWeight={600}
              color="var(--text-tertiary)"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              <InventoryIcon sx={{ fontSize: 13, color: "#1976d2" }} />
              Sub-SKUs ({subSkus.length})
            </Typography>
            <Grid container spacing={1}>
              {subSkus.map((sub) => (
                <SubSku
                  key={sub.uid}
                  sub={sub}
                  isMaster={variant.isMaster}
                  currency={currency}
                  costCurrency={costCurrency}
                />
              ))}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default VariantCard;
